<template>
  <v-card class="gallery-view">
    <gallery-view-toolbar
      :editable="editable"
      :event="event"
      :sensorType="sensorType"
      :canDownload="!event.updating"
      :downloadOptions="downloadOptions"
      :title="title"
      @download="download"
      @update="(attr) => $emit('updateEvent', attr)"
      @close="$emit('close')"
    >
      <template v-if="editable">
        <template v-if="!event.updating">
          <toolbar-button
            class="hidden-sm-and-down"
            icon="fa-edit"
            @click.native.stop="editing = true;"
            data-testid="edit"
          >
            {{ $t('menu.edit') }}
          </toolbar-button>
          <toolbar-button
            icon="fa-trash"
            @click.native.stop="$emit('deleteEvent')"
          >
            {{ $t('menu.delete') }}
          </toolbar-button>
        </template>
        <v-menu offset-y v-else-if="showTimeoutMenu">
          <template #activator="{ on, attrs }">
            <v-tooltip bottom>
              <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <v-btn
                  text
                  class="bar-button"
                  color="primary"
                  v-bind="{ ...attrs, ...tooltipAttrs }"
                  v-on="{ ...on, ...tooltipOn }"
                  data-testid="edit-timeout-warning"
                >
                  <v-badge color="warning" overlap bottom icon="mdi-alert">
                    <v-icon>fa-edit</v-icon>
                  </v-badge>
                  <small>{{ $t('menu.edit') }}</small>
                </v-btn>
              </template>
              {{ $t('msg.timeout.detail') }}
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item @click="retry" data-testid="retry">
              <v-list-item-title>{{ $t('menu.retry') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="editing = true" data-testid="edit-again">
              <v-list-item-title>{{ $t('menu.edit_again') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="confirmDismiss" data-testid="dismiss">
              <v-list-item-title>{{ $t('menu.dismiss') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <toolbar-button icon="fa-refresh" disabled v-else>
          {{ $t('menu.updating') }}
        </toolbar-button>
      </template>
    </gallery-view-toolbar>
    <layout
      class="mb-3"
      :hasPrevious="hasPrevious"
      :hasNext="hasNext"
      @previous="$emit('previous')"
      @next="$emit('next')"
    >
      <template #top>
        <gallery-view-header
          :editable="editable"
          :event="event"
          @updateEvent="$emit('updateEvent', $event)"
        />
      </template>
      <div class="gallery-view__image">
        <div class="gallery-view__image__container">
          <picked-insects-draw
            :imageSrc="event.attachments['display-photo']"
            :detectionsUrl="detectionsUrl"
            :hideCanvas="viewOriginal"
            :pickedDetectionType="pickedDetectionType"
            :originalImageWidth='ORIGINAL_IMAGE_WIDTH'
            :originalImageHeight='ORIGINAL_IMAGE_HEIGHT'
            @save="save"
            ref="drawer"
          />
        </div>
      </div>
      <div class="replacement-alert-container">
        <replace-notification-alert
          :trap_replaced_on="trap_replaced_on"
          :trap_effective_days="trap_effective_days"
        />
      </div>
      <div class="gallery-view__difference">
        <div>
          <div>
            <span>{{ $t('field.total') }}</span>
            <span class="mx-1">:</span>
            <span>{{ event.count }}</span>
          </div>
          <insect-count-difference :event="event" />
        </div>
        <hr />
        <threshold-alert-status
          class="flex-grow-1"
          :event="event"
          :editable="editable"
          :showAlert="event.latest"
          :updatableThreshold="event.latest"
          @updateThreshold="$emit('updateThreshold', $event)"
        />
      </div>
      <template #side>
        <div class="result pa-2">
          <v-row no-gutters align="center" justify="space-between">
            <v-checkbox
              :label="$t('field.original')"
              v-model="viewOriginal"
              hide-details
              color="primary"
              class="mt-0 pt-0"
            />
            <chart-type-select v-model="chartType" />
          </v-row>
          <insect-count-table
            v-if="chartType == 'table'"
            :insectCounts="event.insect_counts"
            :showRadiobutton="!event.updating"
            :pickedDetectionType.sync="pickedDetectionType"
            showFocusButton
            tableMaxHeight="65vh"
            @click:focus="handleFocus"
          />
          <insect-count-by-type-bar-chart
            :insectCounts="event.insect_counts"
            v-if="chartType == 'bar'"
          />
          <top-5-insect-types-pie-chart
            :insectCounts="event.insect_counts"
            v-if="chartType == 'pie'"
          />
        </div>
        <section data-testid="Metadata" class="mt-2 metadata">
          <ul>
            <li v-if="event.ip_address">
              {{ $t('ip_address') }}: {{ event.ip_address }}
            </li>
            <li v-if="event.firmware_version">
              {{ $t('firmware_version') }}: {{ event.firmware_version }}
            </li>
            <li v-if="event.serial_no">
              {{ $t('serial_no') }}: {{ event.serial_no }}
            </li>
          </ul>
        </section>
      </template>
    </layout>
    <slot name="thumbnails" />
    <insect-detections-editor
      :event="event"
      :imageSrc="event.attachments['display-photo']"
      :detectionsUrl="detectionsUrl"
      :originalImageWidth='ORIGINAL_IMAGE_WIDTH'
      :originalImageHeight='ORIGINAL_IMAGE_HEIGHT'
      canViewDetectionDetail
      v-model="editing"
      @updated="$emit('pollEvent')">
      <template #header>
        <content-header class="w-100" :title="title" titleClass="title my-1">
          <template #icon><sensor-icon :sensorType="sensorType" /></template>
          <template #subtitle>
            <div class="title details">
              {{ timestamp.date }}<span class="ml-3" />{{ timestamp.time }} {{ $t('field.total') }}: {{ event.count }}
            </div>
          </template>
        </content-header>
      </template>
    </insect-detections-editor>
    <ConfirmDialog
      v-model="dismissConfirmOpen"
      :message="$t('msg.dismiss.confirm')"
      @yes="executeDismiss"
      data-testid="dismiss-confirm-dialog"
    />
  </v-card>
</template>

<i18n lang="yaml">
ja:
  menu:
    edit: 修正する
    delete: 削除
    updating: 更新中
    retry: 再依頼
    edit_again: 編集し直す
    dismiss: 無視する
  field:
    original: 撮影画像を表示
    total: 頭数合計
  ip_address: ローカルIPアドレス
  firmware_version: ファームウェアのバージョン
  serial_no: シリアルナンバー
  msg:
    download:
      failed: 画像のダウンロードに失敗しました。
    timeout:
      detail: 編集内容の反映に時間がかかっています。操作を選択できます
    retry:
      success: 再依頼を受け付けました
      failed: 再依頼に失敗しました
    dismiss:
      confirm: 編集結果の反映は保証されません。「更新中」の表示のみ解除します。よろしいですか?
      failed: 無視処理に失敗しました

en:
  menu:
    edit: Edit
    delete: Delete
    updating: Updating
    retry: Retry
    edit_again: Edit again
    dismiss: Dismiss
  field:
    original: Show original photo
    total: Total
  ip_address: Local IP Address
  firmware_version: Firmware Version
  serial_no: Serial Number
  msg:
    download:
      failed: Failed to download image.
    timeout:
      detail: The edit is taking longer than expected. Choose an action.
    retry:
      success: Retry accepted.
      failed: Retry failed.
    dismiss:
      confirm: The edit result may not be reflected. This only dismisses the "Updating" display. Continue?
      failed: Dismiss failed.
</i18n>

<script>
import Notifications from '@/mixins/notifications';
import TrainingDataDownload from '@/mixins/trainingDataDownload';
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import ToolbarButton from '@/components/atoms/ToolbarButton';
import ConfirmDialog from '@/components/atoms/ConfirmDialog';
import ChartTypeSelect from '@/components/atoms/ChartTypeSelect';
import InsectCountByTypeBarChart from '@/components/organisms/InsectCountByTypeBarChart';
import Top5InsectTypesPieChart from '@/components/organisms/Top5InsectTypesPieChart';
import GalleryViewHeader from '@/components/GalleryView/GalleryViewHeader';
import GalleryViewToolbar from '@/components/GalleryView/Toolbar';
import Layout from '@/components/GalleryView/Layout';
import ContentHeader from '@/components/ContentHeader';
import InsectCountTable from '@/components/InsectCountTable';
import InsectDetectionsEditor from '@/components/InsectDetectionsEditor';
import InsectCountDifference from '@/components/GalleryView/InsectCountDifference';
import SensorIcon from '@/components/SensorIcon';
import PickedInsectsDraw from '@/components/PickedInsectsDraw';
import { ZoomProvider } from '@/components/Zoom';

import { isEditingTimedOut } from '@/libs/isEditingTimedOut';

import ThresholdAlertStatus from './ThresholdAlertStatus';
import ReplaceNotificationAlert from './ReplaceNotificationAlert';

const ORIGINAL_IMAGE_WIDTH = 5472;
const ORIGINAL_IMAGE_HEIGHT = 3648;

export default {
  name: 'FlyScanView',
  mixins: [
    Notifications,
    TrainingDataDownload,
    ZoomProvider,
    DateTimeFormatting,
  ],
  props: {
    editable: Boolean,
    event: Object,
    sensorType: String,
    hasPrevious: Boolean,
    hasNext: Boolean,
    title: String,
  },
  components: {
    ChartTypeSelect,
    ConfirmDialog,
    ContentHeader,
    GalleryViewHeader,
    GalleryViewToolbar,
    InsectCountByTypeBarChart,
    InsectCountDifference,
    InsectCountTable,
    InsectDetectionsEditor,
    Layout,
    PickedInsectsDraw,
    ReplaceNotificationAlert,
    SensorIcon,
    ThresholdAlertStatus,
    ToolbarButton,
    Top5InsectTypesPieChart,
  },
  computed: {
    timestamp() {
      return {
        date: this.formatDate(this.event.timestamp),
        time: this.formatTime(this.event.timestamp),
      };
    },
    detectionsUrl() {
      return `/api/v2/events/${this.event.id}/insect_detections/photo`;
    },
    difference() {
      return this.event.count_difference;
    },
    showTimeoutMenu() {
      return isEditingTimedOut(this.event, Date.now());
    },
  },
  methods: {
    download: async function (type) {
      switch (type) {
        case 'trainingData': {
          this.downloadAttachment(this.event.id, 'photo');
          break;
        }
        default: {
          this.save(this.$refs.drawer);
          break;
        }
      }
    },
    save: async function (canvas) {
      try {
        await canvas.export('download.png');
      } catch (error) {
        this.notifyError('msg.download.failed');
      }
    },
    handleFocus() {
      this.$refs.drawer.focusNext();
    },
    async retry() {
      const originalStartedAt = this.event.updating_started_at;
      this.$emit('pollEvent', { updating_started_at: Math.floor(Date.now() / 1000) });
      try {
        const attachment = this.event.updating_attachment;
        const data = await this.api.fetchInsectDetectionResult(this.event.id, attachment);
        await this.api.updateInsectDetectionResult(this.event.id, attachment, data.positions);
        this.notifySuccess('msg.retry.success');
      } catch (error) {
        this.$emit('pollEvent', { updating_started_at: originalStartedAt });
        this.notifyError('msg.retry.failed');
      }
    },
    confirmDismiss() {
      this.dismissConfirmOpen = true;
    },
    async executeDismiss() {
      try {
        await this.api.dismissInsectDetectionUpdate(this.event.id);
        this.$emit('pollEvent', {
          updating: false,
          updating_started_at: null,
          updating_attachment: null,
        });
      } catch (error) {
        this.notifyError('msg.dismiss.failed');
      } finally {
        this.dismissConfirmOpen = false;
      }
    },
  },
  data() {
    return {
      editing: false,
      viewOriginal: false,
      pickedDetectionType: 'all',
      chartType: 'table',
      dismissConfirmOpen: false,
      ORIGINAL_IMAGE_WIDTH,
      ORIGINAL_IMAGE_HEIGHT,
    };
  },
  inject: [
    'api',
    'downloadAttachment',
    'trap_effective_days',
    'trap_replaced_on',
  ],
  filters: {
    sign(value) {
      if (value > 0) {
        return `+ ${value}`;
      }
      if (value < 0) {
        return `- ${Math.abs(value)}`;
      }
      return '± 0';
    },
  },
  watch: {
    event(event, prev) {
      this.viewOriginal = false;
      if (event.id === prev.id) {
        this.$refs.drawer.refetch();
      }
    },
  },
};
</script>

<style scoped lang="sass">
@import 'vuetify/src/styles/styles.sass'

.result
  border: 2px solid #ccc

.gallery-view
  &__image
    /* To fix aspect ratio */
    display: inline-block
    height: 0
    width: 80%
    margin: 0 10%
    padding-bottom: calc(80% * (1944 / 2592))
    position: relative

    &__container
      position: absolute
      top: 0
      left: 0
      right: 0
      bottom: 0
    /* - To fix aspect ratio - */

    ::v-deep .loading-image
      @media #{map-get($display-breakpoints, 'md-and-up')}
        min-height: 300px

    ::v-deep .insect-positions-canvas
      height: 100%

  &__difference
    margin: 0 10%
    padding: 1em 0.5em
    border: 2px solid #ccc
    display: flex

    hr
      height: auto
      border-color: #ccc
      border-style: solid
      margin: 0 1.5em

.replacement-alert-container
  margin: 0.5rem 10%
  text-align: left

.metadata
  font-size: 80%
  ul
    padding-left: 0px
    list-style: none
</style>
